<template>
  <div class="page writing-page">
    <section class="page-hero section-wrap writing-hero">
      <div>
        <p class="section-kicker">FIELD NOTES · 18 STORIES AND COUNTING</p>
        <h1>I write to understand<br /><em>how intelligence works.</em></h1>
      </div>
      <p class="page-hero-aside">
        Plain-language explorations of LLM post-training, interpretability,
        attention, and the JavaScript ideas that first made me curious.
      </p>
    </section>

    <section class="featured-story section-wrap">
      <a :href="featured.url" target="_blank" rel="noopener noreferrer" class="featured-cover">
        <div class="cover-grid" aria-hidden="true"></div>
        <span class="cover-number">VOL. 03 / 01</span>
        <div class="rlhf-orbit" aria-hidden="true">
          <span>POLICY</span><span>REWARD</span><span>HUMAN</span>
        </div>
        <div class="featured-cover-copy">
          <p>{{ featured.series }}</p>
          <h2>RLHF</h2>
          <span>READ ARTICLE ↗</span>
        </div>
      </a>

      <div class="featured-details">
        <div class="story-meta"><span>FEATURED</span><span>{{ featured.date }}</span><span>{{ featured.read }}</span></div>
        <h2>{{ featured.title }}</h2>
        <p>{{ featured.summary }}</p>
        <a :href="featured.url" target="_blank" rel="noopener noreferrer" class="text-link">Open on Medium <span>↗</span></a>
      </div>
    </section>

    <section class="story-archive section-wrap">
      <div class="archive-head">
        <div>
          <p class="section-kicker">THE ARCHIVE</p>
          <h2>Ideas, unpacked.</h2>
        </div>
        <div class="filter-row" aria-label="Filter articles">
          <button
            v-for="filter in filters"
            :key="filter"
            :class="{ active: selectedFilter === filter }"
            @click="selectedFilter = filter"
          >{{ filter }}</button>
        </div>
      </div>

      <div class="story-grid">
        <a
          v-for="(article, index) in filteredArticles"
          :key="article.url"
          :href="article.url"
          target="_blank"
          rel="noopener noreferrer"
          class="story-card"
        >
          <div class="story-art" aria-hidden="true">
            <span class="story-index">{{ String(index + 2).padStart(2, '0') }}</span>
            <div class="attention-lines"><i></i><i></i><i></i><i></i></div>
            <strong>{{ article.symbol }}</strong>
          </div>
          <div class="story-card-body">
            <div class="story-meta"><span>{{ article.series }}</span><span>{{ article.read }}</span></div>
            <h3>{{ article.title }}</h3>
            <p>{{ article.summary }}</p>
            <div class="story-footer"><span>{{ article.date }}</span><b>↗</b></div>
          </div>
        </a>
      </div>

      <div class="medium-banner">
        <p>There are more experiments in the archive.</p>
        <a href="https://medium.com/@gaganganapathy" target="_blank" rel="noopener noreferrer">View all 18 stories on Medium <span>↗</span></a>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      selectedFilter: "All",
      filters: ["All", "Post-training", "Interpretability", "JavaScript"],
      featured: {
        title: "Understanding post-training of LLMs: RLHF",
        series: "POST-TRAINING",
        date: "FEB 23, 2026",
        read: "5 MIN READ",
        summary: "A grounded walkthrough of how human preference data becomes a reward signal—and how that signal nudges language models toward more useful behavior.",
        url: "https://medium.com/ai-in-plain-english/understanding-post-training-of-llms-rlhf-ed5ede07d071",
      },
      articles: [
        {
          title: "Understanding post-training of LLMs: DPO",
          series: "Post-training",
          date: "FEB 17, 2026",
          read: "4 MIN",
          symbol: "DPO",
          summary: "Why direct preference optimization can align a model without first learning a separate reward model.",
          url: "https://medium.com/ai-in-plain-english/understanding-post-training-of-llms-dpo-e3978d43aeb0",
        },
        {
          title: "Understanding post-training of LLMs: SFT",
          series: "Post-training",
          date: "FEB 17, 2026",
          read: "4 MIN",
          symbol: "SFT",
          summary: "A clear look at supervised fine-tuning—the foundational first move in shaping a pretrained model.",
          url: "https://medium.com/ai-in-plain-english/understanding-post-training-of-llms-sft-db493262539f",
        },
        {
          title: "Coding the GPT-2 attention layer",
          series: "Interpretability",
          date: "SEP 21, 2025",
          read: "7 MIN",
          symbol: "GPT²",
          summary: "Rebuilding GPT-2 attention in code to make the mechanics behind the abstraction tangible.",
          url: "https://medium.com/ai-in-plain-english/llm-interpretability-coding-the-gpt-2-attention-layer-6a77ef946dad",
        },
        {
          title: "Coding simple attention",
          series: "Interpretability",
          date: "SEP 15, 2025",
          read: "4 MIN",
          symbol: "Q·K",
          summary: "From tokens to weighted context: implementing the smallest useful version of attention from scratch.",
          url: "https://medium.com/ai-in-plain-english/llm-interpretability-coding-simple-attention-8e922adfb68a",
        },
        {
          title: "LLM Interpretability: Attention",
          series: "Interpretability",
          date: "SEP 15, 2025",
          read: "5 MIN",
          symbol: "ATTN",
          summary: "A visual and conceptual guide to the mechanism at the heart of transformer models.",
          url: "https://medium.com/ai-in-plain-english/llm-interpretability-attention-0e47dd96dbf4",
        },
        {
          title: "LLM Interpretability: Logit Lens",
          series: "Interpretability",
          date: "SEP 1, 2025",
          read: "7 MIN",
          symbol: "LOGIT",
          summary: "Peeking inside intermediate layers to see how a transformer’s prediction evolves token by token.",
          url: "https://medium.com/ai-in-plain-english/llm-interpretability-logit-lens-743bbdd670b4",
        },
        {
          title: "LLM Interpretability: Getting started",
          series: "Interpretability",
          date: "SEP 1, 2025",
          read: "3 MIN",
          symbol: "01",
          summary: "A practical entry point into understanding what happens inside large language models.",
          url: "https://medium.com/ai-in-plain-english/llm-interpretability-getting-started-852dd1c88867",
        },
        {
          title: "JS: Implementing Auto-complete",
          series: "JavaScript",
          date: "JUN 11, 2020",
          read: "5 MIN",
          symbol: "TRIE",
          summary: "Building a fast, friendly autocomplete experience and the data structure that powers it.",
          url: "https://medium.com/weekly-webtips/js-implementing-auto-complete-f4c5a5d5c009",
        },
        {
          title: "JS: Currying",
          series: "JavaScript",
          date: "JUN 14, 2020",
          read: "3 MIN",
          symbol: "ƒ(ƒ)",
          summary: "A compact mental model for currying and why function transformation can make code expressive.",
          url: "https://medium.com/@gaganganapathy/js-currying-2b525ebdafd6",
        },
        {
          title: "JS: What’s `this` about?",
          series: "JavaScript",
          date: "JUN 9, 2020",
          read: "3 MIN",
          symbol: "this",
          summary: "Untangling JavaScript’s most context-dependent keyword with examples you can keep in your head.",
          url: "https://medium.com/swlh/js-whats-this-about-4f39a994bd71",
        },
      ],
    };
  },
  computed: {
    filteredArticles() {
      if (this.selectedFilter === "All") return this.articles;
      return this.articles.filter((article) => article.series === this.selectedFilter);
    },
  },
};
</script>
